import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { SearchComponent } from "../../../components/components/js/SearchComponent";
import { TableComponent } from "../../../components/components/js/TableComponent";
import AddstaffModal from "../../../components/components/js/AddModal";
import { HeaderComponent } from "../../../components/components/js/HeaderComponent";
import { LoadingDots } from "../../../components/general";

import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import AuthContext from "../../../context/AuthProvider";

import API_URLS from "../../../constants/apiConstants";
import "react-toastify/dist/ReactToastify.css";
import { downloadCSV, getFilters } from "../../../utils/exportUtils";
import { useTranslation } from "react-i18next";
import QRModal from "../../../components/components/js/QRModal";
import { ADMIN_STAFF_ROUTE } from "../../../routes/RoutesPathes";

const COLUMNS = [
   "name",
   "email",
   "contact",
   "job",
   "hiring", 
   "actions"
];


export const Staff = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const { school } = useContext(AuthContext);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const qrTitle = t("admin_pages.staff.qr_staff_attendance_title");
  const qrDescription = t("admin_pages.staff.qr_staff_attendance_description");

  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const qrUrl = API_URLS.staff_attendance_check(school.id);

  const filters = useMemo(() => getFilters(COLUMNS,t), [t]);

  const { i18n } = useTranslation();
  const formatStaffData = (data) =>
      data.map((staff) => ({
      id: staff.id,
      name: `${staff.FirstName || ""} ${staff.LastName || ""}`.trim(),
      image: staff.ProfileImg || "",
      email: staff.email || "-",
      contact: staff.Phone || "-",
      job: staff.Role || "-",
      hiring: staff.EmpHireDate
        ? new Date(staff.EmpHireDate).toLocaleString(i18n.language, { month: "long" })
        : "-",
    }));


  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get(API_URLS.getStaff(auth.id, school.id));
      const formatted = formatStaffData(response.data);
      setStaff(formatted);
      setFilteredStaff(formatted);
    } catch (error) {
      console.error("GET request error:", error);
      toast.error(t("staff.loadError"));
      // setError(t("staff.loadError"));
      setStaff([]);
      setFilteredStaff([]);
    } finally {
      setLoading(false);
    }
  }, [auth.id, axiosPrivate, school.id, t]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleOpenAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);
  const handleOpenQRModal = () => setShowQRModal(true);
  const handleCloseQRModal = () => setShowQRModal(false);
  // const collapseNav = () => setExpanded(false);

  const handleEdit = (row) => {
    navigate(`details/${row.id}`);
  };

  const handleDelete = async (row) => {
    if (!row) return;

    try {
      setLoading(true);
      await axiosPrivate.delete(`${API_URLS.staff}/${row.id}`);
      toast.success("Staff deleted successfully.");
      fetchStaff();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(t("staff.deleteError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value,filter) => {
    console.log('filter',!value && !filter, filter, value)
    if (!value || !filter) return setFilteredStaff(staff);

    const query = value.toLowerCase();
    const results = staff.filter((s) =>
      s[filter].toLowerCase().includes(query) 
    );
    console.log(results)
    setFilteredStaff(results);
  };

  const handleExport = () => {
    const headers = COLUMNS.filter((col) => col.key !== "actions").map((col) => col.label);
    const data = staff.map((s) => [s.name, s.email, s.contact, s.job, s.hiring]);
    downloadCSV(data, headers, "staff");
  };

  return (
    <div className="px-4" style={{ minHeight: "70vh", marginLeft: "auto" }}>

      <HeaderComponent
        buttonName="add_staff"
        export_fn={handleExport}
        setShow={handleOpenAddModal}
        setGenerate={handleOpenQRModal}
        title="staff"
      />
      <SearchComponent items={filters} onSearch={handleSearch} placeholder={"staff"}/>

      {loading ? (
        <LoadingDots />
      ) : filteredStaff.length > 0 ? (
        <TableComponent
          columns={COLUMNS}
          data={filteredStaff}
          onEdit={handleEdit}
          onDelete={handleDelete}
          page="staff"
          getRowUrl={(row) => `${ADMIN_STAFF_ROUTE}/details/${row.id}`}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center vh-100 fw-bold fs-1">
          {t("staff.noData")}
        </div>
      )}

      <AddstaffModal open={showAddModal} onClose={handleCloseAddModal} title="Staff" />
      <QRModal title={qrTitle} description={qrDescription} qrUrl={qrUrl} open={showQRModal} onClose={handleCloseQRModal}/>
    </div>
  );
};
